import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Truck, Inbox, Settings2, LogOut, Download, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Seo } from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";

const inputCls =
  "w-full bg-surface border border-white/10 focus:border-gold px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300";

const CARRIER_STATUSES = ["new", "in_review", "contacted", "onboarded", "declined"];
const INQUIRY_STATUSES = ["new", "replied", "closed"];

const Badge = ({ status }) => {
  const colors = {
    new: "bg-gold/15 text-champagne border-gold/40",
    in_review: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    contacted: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    onboarded: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    declined: "bg-red-500/10 text-red-300 border-red-500/30",
    replied: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    closed: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${colors[status] || colors.new}`}>
      {(status || "new").replace("_", " ")}
    </span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const Overview = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <p className="text-zinc-500 text-sm">Loading overview…</p>;
  const stats = [
    { label: "Carrier Applications", value: data.carriers_total, testId: "stat-carriers-total" },
    { label: "New Applications", value: data.carriers_new, testId: "stat-carriers-new" },
    { label: "Website Inquiries", value: data.inquiries_total, testId: "stat-inquiries-total" },
    { label: "Unread Inquiries", value: data.inquiries_new, testId: "stat-inquiries-new" },
  ];
  return (
    <div data-testid="admin-overview" className="space-y-10">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testId} className="card-gold p-6">
            <p className="font-display text-4xl font-bold gold-text">{s.value}</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-gold p-6">
          <h3 className="font-display text-lg uppercase text-white mb-4">Recent Carrier Applications</h3>
          {data.recent_carriers.length === 0 && <p className="text-sm text-zinc-500">No applications yet.</p>}
          <ul className="space-y-3">
            {data.recent_carriers.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-zinc-300 truncate">{c.company_name}</span>
                <Badge status={c.status} />
              </li>
            ))}
          </ul>
        </div>
        <div className="card-gold p-6">
          <h3 className="font-display text-lg uppercase text-white mb-4">Recent Inquiries</h3>
          {data.recent_inquiries.length === 0 && <p className="text-sm text-zinc-500">No inquiries yet.</p>}
          <ul className="space-y-3">
            {data.recent_inquiries.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-zinc-300 truncate">{i.name}</span>
                <Badge status={i.status} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Carriers = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () =>
    api
      .get("/admin/carriers", { params: { q, status } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, [status]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/carriers/${id}`, { status: newStatus });
      toast.success("Status updated");
      setSelected((s) => (s ? { ...s, status: newStatus } : s));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const docLabels = { w9: "W-9 Form", insurance: "Certificate of Insurance", authority: "Operating Authority" };

  return (
    <div data-testid="admin-carriers">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          data-testid="carrier-search-input"
          className={`${inputCls} sm:max-w-xs`}
          placeholder="Search company, contact, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <select data-testid="carrier-status-filter" className={`${inputCls} sm:max-w-[200px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {CARRIER_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
        <button data-testid="carrier-search-btn" onClick={load} className="btn-outline-gold !px-5 !py-2.5">Search</button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-zinc-500">No carrier applications found.</p>}
        {items.map((c) => (
          <button
            key={c.id}
            data-testid={`carrier-row-${c.id}`}
            onClick={() => setSelected(c)}
            className="card-gold w-full text-left p-5 flex flex-wrap items-center gap-x-8 gap-y-2"
          >
            <div className="flex-1 min-w-[180px]">
              <p className="font-display text-lg text-white uppercase tracking-wide">{c.company_name}</p>
              <p className="text-xs text-zinc-500">{c.contact_person} · {c.email}</p>
            </div>
            <p className="text-xs text-zinc-400">{c.equipment_type || "—"}</p>
            <p className="text-xs text-zinc-500">{fmtDate(c.created_at)}</p>
            <Badge status={c.status} />
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="carrier-detail-dialog" className="bg-coal border border-gold/30 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase text-white">{selected.company_name}</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mt-2">
                {[
                  ["Contact Person", selected.contact_person],
                  ["Phone", selected.phone],
                  ["Email", selected.email],
                  ["MC Number", selected.mc_number || "—"],
                  ["USDOT Number", selected.usdot_number || "—"],
                  ["Equipment", selected.equipment_type || "—"],
                  ["Trucks", selected.num_trucks || "—"],
                  ["Preferred Lanes", selected.preferred_lanes || "—"],
                  ["Operating Status", selected.operating_status || "—"],
                  ["Submitted", fmtDate(selected.created_at)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k}</p>
                    <p className="text-zinc-200 mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
              {selected.additional_info && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Additional Information</p>
                  <p className="text-sm text-zinc-300 mt-1 leading-relaxed">{selected.additional_info}</p>
                </div>
              )}
              <div className="mt-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Documents (private)</p>
                {Object.keys(selected.documents || {}).length === 0 && <p className="text-sm text-zinc-500">No documents uploaded.</p>}
                <div className="flex flex-wrap gap-3">
                  {Object.entries(selected.documents || {}).map(([key, doc]) => (
                    <a
                      key={key}
                      data-testid={`doc-download-${key}`}
                      href={`${API}/admin/carriers/${selected.id}/documents/${key}`}
                      className="btn-outline-gold !px-4 !py-2 !text-[11px]"
                    >
                      <Download size={13} /> {docLabels[key] || key}
                    </a>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status</p>
                <select
                  data-testid="carrier-status-select"
                  className={`${inputCls} max-w-[180px]`}
                  value={selected.status}
                  onChange={(e) => updateStatus(selected.id, e.target.value)}
                >
                  {CARRIER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Messages = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status: newStatus });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-messages" className="space-y-4">
      {items.length === 0 && <p className="text-sm text-zinc-500">No inquiries yet.</p>}
      {items.map((m) => (
        <div key={m.id} data-testid={`message-card-${m.id}`} className="card-gold p-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
            <p className="font-display text-lg text-white uppercase tracking-wide">{m.name}</p>
            <a href={`mailto:${m.email}`} className="text-xs text-champagne hover:text-gold transition-colors">{m.email}</a>
            {m.phone && <a href={`tel:${m.phone}`} className="text-xs text-zinc-400">{m.phone}</a>}
            {m.fleet_size && <p className="text-xs text-zinc-500">Fleet: {m.fleet_size}</p>}
            <p className="text-xs text-zinc-600">{fmtDate(m.created_at)}</p>
            <Badge status={m.status} />
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed border-l-2 border-gold/50 pl-4">{m.message}</p>
          <div className="mt-4 flex items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status</p>
            <select
              data-testid={`message-status-${m.id}`}
              className={`${inputCls} max-w-[160px]`}
              value={m.status}
              onChange={(e) => updateStatus(m.id, e.target.value)}
            >
              {INQUIRY_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
};

const ContentEditor = () => {
  const { refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setDraft(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!draft) return <p className="text-zinc-500 text-sm">Loading content…</p>;

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSocial = (k, v) => setDraft((d) => ({ ...d, socials: { ...d.socials, [k]: v } }));
  const setListItem = (list, i, k, v) =>
    setDraft((d) => ({ ...d, [list]: d[list].map((item, idx) => (idx === i ? { ...item, [k]: v } : item)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      toast.success("Website content updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-editor" className="space-y-10">
      <div className="card-gold p-6 sm:p-8">
        <h3 className="font-display text-xl uppercase text-white mb-6">Contact Information</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-2">Phone</label>
            <input data-testid="content-phone-input" className={inputCls} value={draft.phone} onChange={(e) => setField("phone", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-2">Email</label>
            <input data-testid="content-email-input" className={inputCls} value={draft.email} onChange={(e) => setField("email", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-2">Location / Service Area</label>
            <input data-testid="content-location-input" className={inputCls} value={draft.location} onChange={(e) => setField("location", e.target.value)} />
          </div>
        </div>
        <h4 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-8 mb-4">Social Media Links</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          {["facebook", "instagram", "linkedin", "tiktok"].map((k) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-2">{k}</label>
              <input
                data-testid={`social-${k}-input`}
                className={inputCls}
                placeholder={`https://${k}.com/…`}
                value={draft.socials?.[k] || ""}
                onChange={(e) => setSocial(k, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="card-gold p-6 sm:p-8">
        <h3 className="font-display text-xl uppercase text-white mb-6">Dispatching Services</h3>
        <div className="space-y-5">
          {(draft.services || []).map((s, i) => (
            <div key={s.key || i} className="grid sm:grid-cols-[220px_1fr] gap-3 items-start">
              <input
                data-testid={`service-title-${i}`}
                className={inputCls}
                value={s.title}
                onChange={(e) => setListItem("services", i, "title", e.target.value)}
              />
              <textarea
                data-testid={`service-desc-${i}`}
                rows={2}
                className={inputCls}
                value={s.description}
                onChange={(e) => setListItem("services", i, "description", e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="card-gold p-6 sm:p-8">
        <h3 className="font-display text-xl uppercase text-white mb-6">Equipment Categories</h3>
        <div className="space-y-5">
          {(draft.equipment || []).map((eq, i) => (
            <div key={eq.key || i} className="flex flex-col sm:flex-row gap-3 sm:items-start">
              <label className="flex items-center gap-2 sm:w-44 shrink-0 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`equipment-active-${i}`}
                  checked={eq.active !== false}
                  onChange={(e) => setListItem("equipment", i, "active", e.target.checked)}
                  className="accent-[#D4AF37] w-4 h-4"
                />
                <span className="text-sm text-white">{eq.title}</span>
              </label>
              <textarea
                data-testid={`equipment-desc-${i}`}
                rows={2}
                className={inputCls}
                value={eq.description}
                onChange={(e) => setListItem("equipment", i, "description", e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="card-gold p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl uppercase text-white">FAQ Content</h3>
          <button
            data-testid="faq-add-btn"
            onClick={() => setDraft((d) => ({ ...d, faqs: [...(d.faqs || []), { question: "", answer: "" }] }))}
            className="btn-outline-gold !px-4 !py-2 !text-[11px]"
          >
            <Plus size={13} /> Add FAQ
          </button>
        </div>
        <div className="space-y-5">
          {(draft.faqs || []).map((f, i) => (
            <div key={i} className="border border-white/5 p-4 space-y-3">
              <div className="flex gap-3">
                <input
                  data-testid={`faq-question-${i}`}
                  className={inputCls}
                  placeholder="Question"
                  value={f.question}
                  onChange={(e) => setListItem("faqs", i, "question", e.target.value)}
                />
                <button
                  data-testid={`faq-remove-${i}`}
                  aria-label="Remove FAQ"
                  onClick={() => setDraft((d) => ({ ...d, faqs: d.faqs.filter((_, idx) => idx !== i) }))}
                  className="p-2.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <textarea
                data-testid={`faq-answer-input-${i}`}
                rows={3}
                className={inputCls}
                placeholder="Answer"
                value={f.answer}
                onChange={(e) => setListItem("faqs", i, "answer", e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <button data-testid="content-save-btn" onClick={save} disabled={saving} className="btn-gold disabled:opacity-50 disabled:pointer-events-none">
        {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
};

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "carriers", label: "Carriers", icon: Truck },
  { key: "messages", label: "Messages", icon: Inbox },
  { key: "content", label: "Site Content", icon: Settings2 },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  if (user === null) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-white">
      <Seo title="Admin Dashboard | B&H Truck Dispatching" description="B&H Truck Dispatching administration." />
      <header className="border-b border-gold/20 bg-coal/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" testId="admin-logo" />
            <p className="font-display uppercase tracking-wide text-sm text-zinc-300">B&amp;H Admin</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-xs text-zinc-500 hidden sm:block">{user.email}</p>
            <button data-testid="admin-logout-btn" onClick={doLogout} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-champagne transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-8 max-w-7xl mx-auto flex gap-1 overflow-x-auto pb-px">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] whitespace-nowrap border-b-2 transition-colors duration-300 ${
                tab === t.key ? "border-gold text-champagne" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="px-4 sm:px-8 max-w-7xl mx-auto py-10">
        {tab === "overview" && <Overview />}
        {tab === "carriers" && <Carriers />}
        {tab === "messages" && <Messages />}
        {tab === "content" && <ContentEditor />}
      </main>
    </div>
  );
}
